import * as React from "react";
import axios from "axios";
import { Loader2, MapPin } from "lucide-react";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

export interface CityApiItem {
  id: number;
  name: string;
  countryId: number;
  regionId?: number;
  country?: {
    id: number;
    name: string;
    iso2?: string;
  };
  region?: {
    id: number;
    name: string;
  };
}

interface CitySelectProps {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  onSelectCity?: (city: {
    name: string;
    province: string;
    countryIso2?: string;
    country?: {
      id: number;
      name: string;
      iso2?: string;
    };
  }) => void;
  countryId?: number;
  disabled?: boolean;
  error?: string;
  placeholder?: string;
  className?: string;
}

export default function CitySelect({
  id = "city-select",
  value,
  onChange,
  onSelectCity,
  countryId,
  disabled = false,
  error,
  placeholder = "Es. Milano",
  className,
}: CitySelectProps) {
  const [open, setOpen] = React.useState(false);
  const [inputValue, setInputValue] = React.useState(value || "");
  const [suggestions, setSuggestions] = React.useState<CityApiItem[]>([]);
  const [loading, setLoading] = React.useState(false);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = React.useState<number>(-1);

  // Sync internal input value if external value changes
  React.useEffect(() => {
    setInputValue(value || "");
  }, [value]);

  // Click outside to close dropdown
  React.useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Debounced search
  React.useEffect(() => {
    if (!inputValue || inputValue.trim().length < 2) {
      setSuggestions([]);
      setLoading(false);
      return;
    }

    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const params: Record<string, any> = {
          search: inputValue.trim(),
        };
        if (countryId && countryId > 0) {
          params.countryId = countryId;
        }
        const res = await axios.get("/Geo/cities", { params });
        if (Array.isArray(res.data)) {
          setSuggestions(res.data);
          setOpen(res.data.length > 0);
          setActiveIndex(-1);
        }
      } catch {
        // silently fail suggestions
      } finally {
        setLoading(false);
      }
    }, 280);

    return () => clearTimeout(timer);
  }, [inputValue, countryId]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setInputValue(val);
    onChange(val);
    setOpen(true);
  };

  const handleSelect = (item: CityApiItem) => {
    setInputValue(item.name);
    onChange(item.name);
    if (onSelectCity) {
      onSelectCity({
        name: item.name,
        province: "",
        countryIso2: item.country?.iso2,
        country: item.country,
      });
    }
    setOpen(false);
    setSuggestions([]);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!open || suggestions.length === 0) {
      if (e.key === "ArrowDown") {
        setOpen(true);
      }
      return;
    }

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((prev) => (prev < suggestions.length - 1 ? prev + 1 : 0));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((prev) => (prev > 0 ? prev - 1 : suggestions.length - 1));
    } else if (e.key === "Enter") {
      if (activeIndex >= 0 && activeIndex < suggestions.length) {
        e.preventDefault();
        handleSelect(suggestions[activeIndex]);
      }
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div ref={containerRef} className="relative w-full">
      <div className="relative flex items-center">
        <Input
          id={id}
          type="text"
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={open}
          aria-controls={`${id}-listbox`}
          aria-invalid={!!error}
          aria-describedby={error ? `${id}-error` : undefined}
          disabled={disabled}
          value={inputValue}
          onChange={handleInputChange}
          onFocus={() => {
            if (suggestions.length > 0) setOpen(true);
          }}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className={cn(
            "pr-9",
            error && "border-destructive focus-visible:ring-destructive",
            className,
          )}
        />
        <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-muted-foreground">
          {loading ? (
            <Loader2
              className="h-4 w-4 animate-spin text-primary"
              aria-hidden="true"
            />
          ) : (
            <MapPin className="h-4 w-4 opacity-50" aria-hidden="true" />
          )}
        </div>
      </div>

      {open && suggestions.length > 0 && (
        <ul
          id={`${id}-listbox`}
          role="listbox"
          aria-label="Suggerimenti città"
          className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-md border bg-popover p-1 text-popover-foreground shadow-md outline-hidden text-sm animate-in fade-in-0 zoom-in-95"
        >
          {suggestions.map((item, idx) => {
            const isHighlighted = idx === activeIndex;
            const regionName = item.region?.name;
            const countryName = item.country?.name;

            return (
              <li
                key={item.id || idx}
                role="option"
                aria-selected={isHighlighted}
                onMouseEnter={() => setActiveIndex(idx)}
                onClick={() => handleSelect(item)}
                className={cn(
                  "relative flex cursor-pointer select-none items-center justify-between rounded-sm px-3 py-2 outline-hidden transition-colors",
                  isHighlighted
                    ? "bg-accent text-accent-foreground"
                    : "hover:bg-accent/60",
                )}
              >
                <div className="flex items-center gap-2 min-w-0">
                  <MapPin
                    className="h-3.5 w-3.5 text-muted-foreground shrink-0"
                    aria-hidden="true"
                  />
                  <span className="font-medium truncate">{item.name}</span>
                </div>

                {(regionName || countryName) && (
                  <span className="text-xs text-muted-foreground truncate pl-2">
                    {[regionName, countryName].filter(Boolean).join(", ")}
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
